import { useState } from "react"
import {
    Box,
    Checkbox,
    Chip,
    IconButton,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { navigate } from "lib/routes/navigate"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { createSkillMap } from "routes/skill-mapper/controller/create-skill-map"
import { archiveSkillMap } from "routes/skill-mapper/controller/archive-skill-map"
import { restoreSkillMap } from "routes/skill-mapper/controller/restore-skill-map"
import { getUniqueCopyName } from "./get-unique-copy-name"
import { SkillMapRateCardCell } from "./skill-map-rate-card-cell"
import { LinkedScenariosCell } from "../linked-scenarios-cell"
import { SkillMapArchiveMessage } from "./skill-map-archive-message"

export function SkillMapRow({ item, rateCardsBySkillMap, scenariosBySkillMap, onAfterRestore }) {
    const trades = item.trades || {}
    const tradeCount = Object.keys(trades).length
    const [anchorEl, setAnchorEl] = useState(null)
    const linkedRateCards = (rateCardsBySkillMap || {})[item.id] || []
    const linkedScenarios = (scenariosBySkillMap || {})[item.id] || []
    const { selectedIds, onToggleSelect, canManageOptimiser } = useBoundContext()
    const isSelected = selectedIds?.has(item.id)

    return (
        <ListItemButton
            data-cy="skill-map-row"
            sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}
            onClick={navigateToMap}
        >
            <ListItemBox sx={{ gap: 3 }}>
                <If value={canManageOptimiser}>
                    <Checkbox
                        size="small"
                        checked={isSelected || false}
                        onChange={handleToggleCheck}
                        onClick={stopProp}
                        sx={{ p: 0, mr: -1, flexShrink: 0 }}
                    />
                </If>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    {/* minWidth:0 lets the name column shrink so a long unbroken name ellipsises instead of overflowing the row */}
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                        <TruncatedTypography variant="body2" fontWeight={500} sx={{ minWidth: 0 }}>
                            {item.name}
                        </TruncatedTypography>
                        <If value={item.isArchived}>
                            <Chip
                                label="Archived"
                                size="small"
                                color="warning"
                                variant="outlined"
                                // flexShrink:0 keeps the chip fully visible while the name truncates
                                sx={{ height: 20, fontSize: 11, flexShrink: 0 }}
                            />
                        </If>
                    </Stack>
                    <If value={item.description}>
                        <TruncatedTypography variant="caption" color="text.secondary" sx={{ fontSize: 14 }}>
                            {item.description}
                        </TruncatedTypography>
                    </If>
                </Box>
                <Box sx={{ width: 160 }}>
                    <SkillMapRateCardCell rateCards={linkedRateCards} />
                </Box>
                <Box sx={{ width: 140 }}>
                    <LinkedScenariosCell scenarios={linkedScenarios} />
                </Box>
                <Box sx={{ width: 80, textAlign: "right" }}>
                    <Typography variant="body1">{tradeCount}</Typography>
                </Box>
                <Box sx={{ width: 120 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {formatDate(item.created)}
                    </Typography>
                </Box>
                <If value={canManageOptimiser}>
                    <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
                        <IconButton size="small" onClick={openMenu} aria-label="Row actions">
                            <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                            <If value={!item.isArchived}>
                                <MenuItem onClick={handleEdit}>
                                    <ListItemIcon>
                                        <Iconify icon="solar:pen-bold" width={20} />
                                    </ListItemIcon>
                                    <ListItemText>Edit</ListItemText>
                                </MenuItem>
                            </If>
                            <MenuItem onClick={handleDuplicate}>
                                <ListItemIcon>
                                    <Iconify icon="solar:copy-bold" width={20} />
                                </ListItemIcon>
                                <ListItemText>Duplicate</ListItemText>
                            </MenuItem>
                            <If value={!item.isArchived}>
                                <MenuItem onClick={handleArchive}>
                                    <ListItemIcon>
                                        <Iconify icon="solar:archive-bold" width={20} />
                                    </ListItemIcon>
                                    <ListItemText>Archive</ListItemText>
                                </MenuItem>
                                <Else>
                                    <MenuItem onClick={handleRestore}>
                                        <ListItemIcon>
                                            <Iconify icon="solar:restart-bold" width={20} />
                                        </ListItemIcon>
                                        <ListItemText>Restore</ListItemText>
                                    </MenuItem>
                                </Else>
                            </If>
                        </Menu>
                    </Box>
                    <Else>
                        <Box sx={{ width: 40 }} />
                    </Else>
                </If>
            </ListItemBox>
        </ListItemButton>
    )

    function openMenu(e) {
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }

    function closeMenu(e) {
        if (e) e.stopPropagation()
        setAnchorEl(null)
    }

    function stopProp(e) {
        e.stopPropagation()
    }

    function handleToggleCheck(e) {
        e.stopPropagation()
        onToggleSelect?.(item.id)
    }

    function navigateToMap() {
        navigate(`/app/optimiser/skill-mappings/${item.id}`)
    }

    function handleEdit(e) {
        e.stopPropagation()
        closeMenu()
        navigate(`/app/optimiser/skill-mappings/${item.id}`)
    }

    async function handleDuplicate(e) {
        e.stopPropagation()
        closeMenu()
        await busyWhile(async () => {
            const copyName = await getUniqueCopyName(item.name)
            return createSkillMap({
                name: copyName,
                startingPoint: "existing",
                sourceSkillMapId: item.id,
            })
        }, "Duplicating skill mapping...")
    }

    async function handleArchive(e) {
        e.stopPropagation()
        closeMenu()
        const ok = await confirm(
            <SkillMapArchiveMessage name={item.name} linkedRateCards={linkedRateCards} />,
            "Archive Skill Mapping"
        )
        if (!ok) return
        await busyWhile(() => archiveSkillMap({ id: item.id }), "Archiving skill mapping...")
        successSnackbar(`Skill map "${item.name}" archived`)
    }

    async function handleRestore(e) {
        e.stopPropagation()
        closeMenu()
        await busyWhile(() => restoreSkillMap({ id: item.id }), "Restoring skill mapping...")
        successSnackbar(`Skill map "${item.name}" restored`)
        onAfterRestore?.()
    }
}

function formatDate(date) {
    if (!date) return "-"
    const d = new Date(date)
    const day = d.getDate()
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    const month = months[d.getMonth()]
    const year = d.getFullYear()
    return `${day} ${month}, ${year}`
}
